import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import Accordion from 'components/common/accordion'
import Typo from 'constants/typography'
import { PDP_INTERACT_ELEM_EVENT_NAME } from 'components/pdp/const'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { segmentTrackPDPInteract } from 'lib/utils/product'
import { customFlagValue } from 'lib/utils/handle-flag/const'
import ProductUSP from 'components/pdp/interactions/product-usp'
import ProductCareInstructions from './product-care-instructions'
import ProductIngredients from './product-ingredients'
import ProductMaterials from './product-materials'
import ProductSustainable from './product-sustainable'

const ProductDescriptions = ({
  internationalization: { country },
  product,
  t,
  shippingAccordionId,
  clarifyDelivery,
  hideShipping,
  compactServices,
  deliveryPolicyShown,
}) => {
  const pdpSustainabilityAccordionFlagValue = customFlagValue({
    defaultValue: false,
  }) // TODO: force flag value to handle with flagsmith later (SustainabilityProductPage-20220822 old flag name)

  const { ingredients, materials, sustainable } = product

  const shouldDisplaySustainableAccordion =
    pdpSustainabilityAccordionFlagValue && !!sustainable?.length

  const trackAccordionClick = (isAccordionOpen, accordionName) =>
    isAccordionOpen &&
    segmentTrackPDPInteract(
      product,
      country,
      PDP_INTERACT_ELEM_EVENT_NAME[accordionName],
    )

  return (
    <div className="pdp__descriptions-container">
      {ingredients?.main && (
        <Accordion
          titleTypography={Typo.subtitle2}
          title={hasLocizeTranslation(
            t,
            'PDP_ACCORDION_INGREDIENTS',
            'Ingredients',
          )}
        >
          <ProductIngredients product={product} />
        </Accordion>
      )}
      {(materials?.fabrics || materials?.care_instructions) && (
        <Accordion
          titleTypography={Typo.subtitle2}
          title={hasLocizeTranslation(
            t,
            'PDP_ACCORDION_MATERIALS',
            'Care & Materials',
          )}
          onChange={(isAccordionOpen) => {
            trackAccordionClick(isAccordionOpen, 'material')
            trackAccordionClick(isAccordionOpen, 'careInstruction')
          }}
        >
          <React.Fragment>
            {materials?.care_instructions && (
              <ProductCareInstructions details={materials.care_instructions} />
            )}
            {materials?.fabrics && (
              <ProductMaterials details={materials.fabrics} />
            )}
          </React.Fragment>
        </Accordion>
      )}
      {shouldDisplaySustainableAccordion && (
        <Accordion
          titleTypography={Typo.subtitle2}
          title={hasLocizeTranslation(
            t,
            'PDP_ACCORDION_SUSTAINABILITY',
            'Sustainability',
          )}
          onChange={(isAccordionOpen) =>
            trackAccordionClick(isAccordionOpen, 'sustainable')
          }
        >
          <ProductSustainable
            onLinkClick={trackAccordionClick}
            sustainableItem={sustainable}
          />
        </Accordion>
      )}
      {!hideShipping && compactServices && (
        <ProductUSP
          compact
          policyFirst
          deliveryPolicyShown={deliveryPolicyShown}
        />
      )}
      {!hideShipping && !compactServices && (
        <Accordion
          titleTypography={Typo.subtitle2}
          isExpanded
          id={shippingAccordionId}
          title={hasLocizeTranslation(
            t,
            'PDP_ACCORDION_SHIPPING_RETURNS',
            'Shipping & Returns',
          )}
        >
          <ProductUSP policyFirst={clarifyDelivery} />
        </Accordion>
      )}
    </div>
  )
}

ProductDescriptions.propTypes = {
  compactServices: PropTypes.bool,
  deliveryPolicyShown: PropTypes.bool,
  hideShipping: PropTypes.bool,
  shippingAccordionId: PropTypes.string,
  clarifyDelivery: PropTypes.bool,
  internationalization: PropTypes.shape({
    country: PropTypes.string,
  }).isRequired,
  product: PropTypes.shape({
    is_italist: PropTypes.bool,
    category: PropTypes.shape({}),
    description: PropTypes.string,
    id_product: PropTypes.number,
    ingredients: PropTypes.shape({
      main: PropTypes.string,
      free_from: PropTypes.string,
    }),
    materials: PropTypes.shape({
      fabrics: PropTypes.shape({
        fabrics: PropTypes.arrayOf(PropTypes.shape({})),
      }),
      care_instructions: PropTypes.shape({}),
    }),
    name: PropTypes.string,
    sustainable: PropTypes.arrayOf(PropTypes.string),
    type: PropTypes.string,
    variations: PropTypes.arrayOf(
      PropTypes.shape({
        id_product: PropTypes.number,
        name: PropTypes.string,
        description: PropTypes.string,
        display_color: PropTypes.string,
        pattern_image: PropTypes.string,
      }),
    ),
  }).isRequired,
  t: PropTypes.func.isRequired,
}

ProductDescriptions.defaultProps = {
  compactServices: false,
  deliveryPolicyShown: false,
  hideShipping: false,
  shippingAccordionId: undefined,
  clarifyDelivery: false,
}

export default compose(
  connect((state) => ({
    internationalization: state.internationalization,
  })),
  withI18next(),
)(ProductDescriptions)
